import { useEffect, useMemo, useRef, useState } from 'react'
import type { CategoryGroup, BudgetCategory } from '@/features/budget/types'

interface Props {
  groups: CategoryGroup[]
  value: string | null
  onChange: (categoryId: string | null) => void
  /** Render the unselected state as italic / muted to call attention
   *  to uncategorized rows. */
  highlightEmpty?: boolean
}

interface FlatItem {
  groupName: string
  category: BudgetCategory
}

function flatten(groups: CategoryGroup[]): FlatItem[] {
  return groups
    .filter((g) => !g.isHidden)
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .flatMap((g) =>
      g.categories
        .filter((c) => !c.isHidden)
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map((c) => ({ groupName: g.name, category: c })),
    )
}

/**
 * Compact category typeahead. YNAB-style: groups visually, filtered
 * by substring. Used inline in transaction rows.
 *
 * Closes on outside click + Esc. Enter selects the highlighted item;
 * arrows nav.
 */
export function CategoryDropdown({ groups, value, onChange, highlightEmpty }: Props) {
  const flat = useMemo(() => flatten(groups), [groups])
  const [open, setOpen] = useState(false)
  const [filter, setFilter] = useState('')
  const [highlight, setHighlight] = useState(0)
  const rootRef = useRef<HTMLDivElement>(null)

  const filtered = useMemo(() => {
    if (!filter) return flat
    const f = filter.toLowerCase()
    return flat.filter((item) =>
      `${item.groupName} ${item.category.name}`.toLowerCase().includes(f),
    )
  }, [filter, flat])

  const current = flat.find((f) => f.category.id === value)
  const label = current?.category.name ?? 'Uncategorized'

  useEffect(() => {
    if (!open) return
    function onDocClick(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [open])

  function commit(categoryId: string | null) {
    onChange(categoryId)
    setOpen(false)
    setFilter('')
  }

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`w-full truncate rounded px-2 py-1 text-left text-sm hover:bg-zinc-100 dark:hover:bg-zinc-800 ${
          value || !highlightEmpty ? '' : 'italic text-underfunded'
        }`}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {label}
      </button>
      {open && (
        <div
          role="listbox"
          className="absolute left-0 top-full z-20 mt-1 max-h-72 w-72 overflow-auto rounded-md border border-zinc-200 bg-white shadow-lg dark:border-zinc-700 dark:bg-zinc-900"
          onKeyDown={(e) => {
            if (e.key === 'Escape') setOpen(false)
            else if (e.key === 'ArrowDown') {
              e.preventDefault()
              setHighlight((h) => Math.min(h + 1, filtered.length - 1))
            } else if (e.key === 'ArrowUp') {
              e.preventDefault()
              setHighlight((h) => Math.max(h - 1, 0))
            } else if (e.key === 'Enter' && filtered[highlight]) {
              e.preventDefault()
              commit(filtered[highlight].category.id)
            }
          }}
        >
          <div className="border-b border-zinc-200 p-2 dark:border-zinc-700">
            <input
              autoFocus
              value={filter}
              onChange={(e) => {
                setFilter(e.target.value)
                setHighlight(0)
              }}
              placeholder="Filter…"
              className="w-full rounded border border-zinc-300 bg-white px-2 py-1 text-sm dark:border-zinc-700 dark:bg-zinc-950"
              aria-label="Filter categories"
            />
          </div>
          <button
            type="button"
            role="option"
            aria-selected={value === null}
            onClick={() => commit(null)}
            className="block w-full px-3 py-1 text-left text-xs italic text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-800"
          >
            Uncategorized
          </button>
          {filtered.length === 0 ? (
            <div className="px-3 py-4 text-center text-xs text-zinc-500">No matches</div>
          ) : (
            filtered.map((item, i) => (
              <button
                key={item.category.id}
                type="button"
                role="option"
                aria-selected={item.category.id === value}
                onClick={() => commit(item.category.id)}
                onMouseEnter={() => setHighlight(i)}
                className={`block w-full px-3 py-1 text-left text-sm ${
                  i === highlight ? 'bg-zinc-100 dark:bg-zinc-800' : ''
                }`}
              >
                <span className="text-xs text-zinc-500">{item.groupName} / </span>
                {item.category.name}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  )
}
